/**
 * Pilot-phase blocking overlay for the homepage.
 *
 * Shown once per browser session — dismissal stored in sessionStorage so
 * repeat in-tab navigations don't keep hitting the wall, but a fresh tab
 * (genuinely new visit) sees it again.
 *
 * Renders after a 300ms delay so the page paints first → the overlay
 * "introduces itself" rather than greeting the user with a wall of text.
 *
 * Keyboard / a11y: Esc and outside-click are intentionally disabled. The
 * user MUST hit the explicit "I understand and accept" button to dismiss.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ShieldCheck } from "lucide-react";

const SESSION_KEY = "sarathix_pilot_overlay_v1_seen";

export default function PilotOverlay() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let storage = null;
    try { storage = window.sessionStorage; } catch (_) { /* sandboxed */ }
    if (storage && storage.getItem(SESSION_KEY) === "1") return;

    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Lock background scroll while overlay is open.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [visible]);

  const handleDismiss = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch (_) { /* noop */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 sm:px-4 sm:py-8 overflow-y-auto"
          data-testid="pilot-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: 0.05 }}
            className="relative w-full max-w-xl bg-sarathi-surface border border-sarathi-primary/30 rounded-2xl p-4 sm:p-7 shadow-[0_0_60px_rgba(255,90,31,0.15)]"
          >
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-sarathi-primary/15 border border-sarathi-primary/30 flex items-center justify-center shrink-0">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-sarathi-primary" />
              </div>
              <p className="text-sarathi-primary text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em]">
                A note about our pilot
              </p>
            </div>

            <h2 className="font-outfit text-lg sm:text-2xl font-bold text-white leading-snug sm:leading-tight mb-3 sm:mb-5 break-words">
              One car. One driver.<br className="sm:hidden" /> Every ride personally handled.
            </h2>

            <div className="space-y-2.5 sm:space-y-4 text-sarathi-text-secondary text-[12.5px] sm:text-[15px] leading-relaxed">
              <p>
                Sarathi-X begins on{" "}
                <span className="text-white font-medium">18th June 2026, Thursday,</span>{" "}
                with one car, a handful of rides a day, and a simple goal: to
                make every ride the easiest part of someone's day. For the
                first two weeks, our co-founder,{" "}
                <span className="text-white font-medium">Vishal Rao,</span> will
                be behind the wheel himself. After that, rides will be handled
                by our carefully selected and trained driver — same car, same
                standards, same attention to detail.
              </p>
              <p>
                We've chosen to start small deliberately because trust isn't
                something you launch with but is something you earn, ride by
                ride. Availability is limited and while we'll do everything
                possible to confirm your ride, we may occasionally have to
                decline a booking if we're already committed. In such
                instances, we'll tell you immediately and suggest the next
                available slot.
              </p>
              <p className="text-white">
                If you're riding with us during this pilot, it means the world
                to us. You're not just a booking. You're the reason Sarathi-X
                exists.
              </p>
            </div>

            <div className="mt-4 sm:mt-6 pt-3 sm:pt-5 border-t border-white/5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-4">
              <div className="flex items-center gap-2 text-[11px] sm:text-xs text-sarathi-text-muted">
                <ShieldCheck className="w-3.5 h-3.5 text-sarathi-primary/80 shrink-0" />
                <span>By continuing you acknowledge our pilot terms.</span>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="w-full sm:w-auto bg-sarathi-primary hover:bg-sarathi-primary-hover text-white px-5 py-2.5 sm:py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap"
                data-testid="pilot-overlay-accept-btn"
              >
                I understand and accept
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
